import * as React from "react";
import { useApiService, useTl } from "../../../hooks";
import { JudgeApi, JudgeTransientBreedApi, JudgeTransientBreedGridDto, JudgeTransientBreedSearch } from "../../../api";
import { ButtonContainer, FieldSet, IDataTableColumn, showSuccess, useGridState, useSearchApi } from "nsitools-react";
import { useAuth, useDialog } from "../../../contexts";
import { Button, Checkbox, Intent } from "@blueprintjs/core";
import { ETLCodes } from "../../../locales";
import { IconNames } from "@blueprintjs/icons";
import { Link } from "react-router-dom";
import { ERoutes } from "../../../ERoutes";
import { ExportButton, CustomDataTable } from "../../../components";

export interface IJudgeBreedValidationPageProps {}

export const JudgeBreedValidationPage: React.FunctionComponent<IJudgeBreedValidationPageProps> = (props) => {
  const api = useApiService(JudgeTransientBreedApi);
  const { t, tUnsafe } = useTl();
  const { showDialog } = useDialog();
  const [sObjState, setsObjState] = React.useState<any>();
  const selectionList = React.useMemo(() => [], []);
  const [selectAll, setselectAll] = React.useState(false);
  const [Actionloading, setActionloading] = React.useState(false);
  const { user } = useAuth();

  const tableState = useGridState<any>({
    serverMode: true,
    availablePageSizes: [10, 15, 25],
    enablePagination: true,
    enableFilter: true,
    sortKeys: { judgeName: "ASC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: JudgeTransientBreedSearch) => {
      setsObjState(sObj);
      return api.baseSearch(sObj);
    },
    [api, user.userId]
  );

  const { search, loading, gridResults } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length);
      selectionList.push(...tableState?.data?.map((r) => r.breedJudgeTransientId));
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
  }, [selectAll, selectionList]);

  const columnCheckBox = React.useCallback(
    (row: JudgeTransientBreedGridDto) => {
      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.breedJudgeTransientId)}
          onChange={() => {
            if (selectionList.includes(row.breedJudgeTransientId)) {
              const index = selectionList.findIndex((e) => e === row.breedJudgeTransientId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.breedJudgeTransientId);
            }
          }}
        />
      );
    },
    [selectionList]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox onChange={toggleSelectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: columnCheckBox,
      },
      {
        header: () => t(ETLCodes.JudgeName),
        fieldName: "judgeName",
        render: (row: JudgeTransientBreedGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/judge/view`}>
            {row.judgeName}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.BreedGroup),
        fieldName: "breedGroupName",
      },
      {
        header: () => t(ETLCodes.Breed),
        fieldName: "breedName",
        render: (row: JudgeTransientBreedGridDto) => (
          <Link target="_blank" to={`${ERoutes.breeds}/${row.breedId}/detail/view`}>
            {row.breedName}
          </Link>
        ),
      },
    ],
    [columnCheckBox, t, tUnsafe, toggleSelectAll]
  );

  const action = React.useCallback(
    async (requestList: number[]) => {
      setActionloading(true);
      if (requestList.length === 0) {
        showDialog({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.ValidateElementsQuestion),
          title: t(ETLCodes.Validate),
          onConfirmed: async () => {
            await api.validateJudgeTransientBreeds(requestList);
            search();
          },
        });
      }
      setActionloading(false);
    },
    [api, search, showDialog, t, user.userId]
  );

  const refuse = React.useCallback(
    (requestList: number[]) => {
      setActionloading(true);
      if (requestList.length === 0) {
        showDialog({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.DeleteJudgeTempBreed),
          title: t(ETLCodes.Warning),
          onConfirmed: async () => {
            await api.removeJudgeTransientBreeds(requestList);
            showSuccess(t(ETLCodes.SaveSuccess));
            search();
          },
        });
        setActionloading(false);
      }
    },
    [api, search, showDialog, t]
  );

  return (
    <FieldSet
      title={t(ETLCodes.BreedJudgeToValidate)}
      rightElement={
        <ButtonContainer>
          <Button
            text={t(ETLCodes.Validate)}
            onClick={() => action(selectionList)}
            intent={Intent.PRIMARY}
            minimal={true}
            icon={IconNames.TICK_CIRCLE}
            loading={Actionloading}
          />
          <Button
            text={t(ETLCodes.Refuse)}
            onClick={() => refuse(selectionList)}
            intent={Intent.PRIMARY}
            minimal={true}
            icon={IconNames.DELETE}
            loading={Actionloading}
          />
          <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
        </ButtonContainer>
      }
    >
      <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
    </FieldSet>
  );
};
